import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import MobileViewContainer from "../../../Components/MobileViewContainer";

import Navbar from "../../../Components/Common/navbar";
import { ticketService } from "../../../api/services/ticket.service";
import { useTicketCartStore } from "../../../store/ticketCartStore";
import {
  formatOrderDoneDate,
  getOrderDoneResults,
} from "../orderDoneDisplay.mjs";

const MultiOrderDone = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const clearCart = useTicketCartStore((state) => state.clearCart);

  const [orders, setOrders] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    clearCart();

    const fetchData = async () => {
      if (!orderId) {
        setOrders([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setHasError(false);
      try {
        const response = await ticketService.getTicketOrderDoneMultiByOrderId(orderId);
        setOrders(getOrderDoneResults(response.data));
      } catch (error) {
        console.log(error);
        setHasError(true);
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [clearCart, navigate, orderId]);

  // [임시] 백엔드 /ticket/user-ticket-app/?order_id= 가 정상 데이터를 반환하기 전까지는
  // 빈 완료 화면("예약 내역이 없습니다")을 노출하지 않고 마이페이지로 보낸다.
  // 백엔드에서 order_id 조회가 정상화되면 이 useEffect 블록만 제거하면 완료 화면이 그대로 노출된다.
  React.useEffect(() => {
    if (!isLoading && !hasError && orders.length === 0) {
      navigate("/my-page", { replace: true });
    }
  }, [isLoading, hasError, orders.length, navigate]);

  // 묶음 결제는 bundle_id가 같은 개별 티켓 N개로 내려오므로, 묶음 단위로 합쳐 수량/금액으로 표시한다.
  const groupedOrders = React.useMemo(() => {
    const groups = [];
    const indexByKey = new Map();

    for (const order of orders) {
      const key = order?.bundle_id ? `bundle:${order.bundle_id}` : `id:${order?.id}`;
      const amount = Number(order?.amount) || 0;
      const hasAmount = typeof order?.amount === "number";

      if (indexByKey.has(key)) {
        const group = groups[indexByKey.get(key)];
        group.quantity += 1;
        group.totalAmount += amount;
        group.hasAmount = group.hasAmount || hasAmount;
      } else {
        indexByKey.set(key, groups.length);
        groups.push({
          id: order?.id,
          name: order?.name,
          expiration_date: order?.expiration_date,
          orderDate: order?.orderDate,
          quantity: 1,
          totalAmount: amount,
          hasAmount,
        });
      }
    }

    return groups;
  }, [orders]);

  const renderStatusMessage = (message) => (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm font-semibold text-gray-500 shadow-[0_6px_18px_rgba(0,0,0,0.04)]">
      {message}
    </div>
  );

  const formatReceiptDate = (value) => {
    const formatted = formatOrderDoneDate(value);
    return formatted === "-" ? formatted : formatted.replace(/-/g, ".");
  };

  const renderOrders = () => {
    if (isLoading) return renderStatusMessage("주문 내역을 불러오는 중입니다.");
    if (hasError) return renderStatusMessage("주문 내역을 불러오지 못했습니다.");
    if (!groupedOrders.length) return renderStatusMessage("주문 내역이 없습니다.");

    return (
      <div className="flex flex-col gap-4">
        {groupedOrders.map((order, key) => (
          <div key={order.id ?? key} className="relative rounded-t-2xl bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
            <div className="p-5 pb-6">
              {key === 0 ? (
                <div className="mb-4 flex flex-col items-center text-center">
                  <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="m-0 font-semibold text-green-600">결제가 완료되었습니다</p>
                  {order.orderDate ? (
                    <p className="m-0 mt-1 text-sm text-gray-600">{formatReceiptDate(order.orderDate)} 결제</p>
                  ) : null}
                </div>
              ) : null}

              <div className="flex flex-col items-center border-t-2 border-b-2 border-dashed border-gray-200 py-4">
                <span className="mb-1 text-xs text-gray-900">주문번호</span>
                <p className="m-0 text-3xl font-black tabular-nums text-gray-900" style={{ fontFamily: "Gmarket-B" }}>#{order.id}</p>
              </div>

              <div className="space-y-2 pt-4">
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="shrink-0 text-gray-900">상품명</span>
                  <span className="text-right font-bold text-gray-900">{order.name}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="shrink-0 text-gray-900">수량</span>
                  <span className="text-right font-bold text-gray-900">{order.quantity}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="shrink-0 text-gray-900">만료일</span>
                  <span className="text-right font-bold text-gray-900">{formatReceiptDate(order.expiration_date)}</span>
                </div>
              </div>
            </div>

            {order.hasAmount ? (
              <>
                <div className="relative">
                  <div className="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-gray-100"></div>
                  <div className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-gray-100"></div>
                  <div className="mx-4 border-t-2 border-dashed border-gray-200"></div>
                </div>

                <div className="flex items-center justify-between px-5 pb-3 pt-5">
                  <span className="text-base font-bold text-gray-700">총 결제 금액</span>
                  <strong className="text-2xl font-black text-[#e16036] tabular-nums" style={{ fontFamily: "Gmarket-B" }}>
                    {order.totalAmount.toLocaleString()}원
                  </strong>
                </div>
              </>
            ) : null}

            <svg className="block w-full" height="10" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
              <polygon points="0,10 0,0 5,10 10,0 15,10 20,0 25,10 30,0 35,10 40,0 45,10 50,0 55,10 60,0 65,10 70,0 75,10 80,0 85,10 90,0 95,10 100,0 100,10" fill="#f3f4f6" />
            </svg>
          </div>
        ))}

        <div className="flex flex-col items-center gap-2 px-4 pt-5 pb-3 text-center">
          <p className="m-0 text-base text-gray-900">결제가 완료되었습니다!</p>
          <p className="m-0 text-sm text-gray-900">결제 내역은 마이페이지에서 확인 가능합니다.</p>
        </div>
      </div>
    );
  };

  return (
    <MobileViewContainer>
      <Navbar hideHeader />

      <div className="sticky top-0 z-10 flex h-18 items-center justify-center border-b border-gray-100 bg-white px-5">
        <h1 className="m-0 text-xl font-bold" style={{ fontFamily: "Gmarket-B" }}>결제 완료</h1>
      </div>

      <div
        className="flex flex-1 flex-col bg-gray-100 px-4 pt-5"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)" }}
      >
        {renderOrders()}

        <div className="mt-auto flex flex-col gap-2 rounded-2xl bg-white px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
          <button
            type="button"
            onClick={() => navigate("/my-page")}
            className="w-full rounded-xl bg-[#e16036] px-4 py-3.5 text-base font-bold text-white shadow-[0_4px_14px_rgba(225,96,54,0.25)] transition-colors hover:bg-[#cc5530]"
          >
            결제 내역 확인하기
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="w-full rounded-xl border-2 border-[#e16036] bg-white px-4 py-3 text-sm font-bold text-[#e16036] transition-colors hover:bg-[#fff4ee]"
          >
            홈으로
          </button>
        </div>
      </div>
    </MobileViewContainer>
  );
};
export default MultiOrderDone;
